"use client";

import { useState, useRef, useEffect } from "react";
// useState still used by MiniPlayer

interface CaseStudyPlayerProps {
  originalAudioUrl: string;
  deliveredAudioUrl: string;
  originalLyrics: string;
  deliveredLyrics: string;
  songName: string;
  customerReview?: {
    rating: number;
    body: string;
    author: string;
  };
}

function formatTime(seconds: number) {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function MiniPlayer({
  url,
  label,
  accentClass,
}: {
  url: string;
  label: string;
  accentClass: string;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateDuration = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const onTime = () => {
      setCurrentTime(audio.currentTime);
      updateDuration();
    };
    const onEnded = () => setIsPlaying(false);

    // Check if already loaded
    if (audio.readyState >= 1) {
      updateDuration();
    }

    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("canplay", updateDuration);
    audio.addEventListener("durationchange", updateDuration);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("canplay", updateDuration);
      audio.removeEventListener("durationchange", updateDuration);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const t = parseFloat(e.target.value);
    audio.currentTime = t;
    setCurrentTime(t);
  };

  return (
    <div className="bg-base-200 rounded-xl p-4">
      <audio ref={audioRef} src={url} preload="none" />
      <div className="flex items-center gap-2 mb-2">
        <span
          className={`badge badge-sm ${accentClass} font-bold text-xs uppercase`}
        >
          {label}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          className={`btn btn-circle btn-sm ${
            accentClass === "badge-primary"
              ? "btn-primary"
              : "btn-secondary"
          }`}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg
              className="w-4 h-4"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>
        <span className="text-xs text-base-content/60 min-w-[36px]">
          {formatTime(currentTime)}
        </span>
        <input
          type="range"
          min="0"
          max={duration || 1}
          value={currentTime}
          onChange={seek}
          className={`range range-xs flex-1 ${
            accentClass === "badge-primary"
              ? "range-primary"
              : "range-secondary"
          }`}
        />
        <span className="text-xs text-base-content/60 min-w-[36px]">
          {duration > 0 ? formatTime(duration) : "--:--"}
        </span>
      </div>
    </div>
  );
}

export default function CaseStudyPlayer({
  originalAudioUrl,
  deliveredAudioUrl,
  originalLyrics,
  deliveredLyrics,
  songName,
  customerReview,
}: CaseStudyPlayerProps) {
  return (
    <div className="my-8 border border-base-300 rounded-2xl overflow-hidden bg-base-100">
      {/* Header */}
      <div className="bg-base-200 px-6 py-4 border-b border-base-300">
        <h3 className="text-lg font-bold text-base-content">
          Listen & Compare
        </h3>
        <p className="text-sm text-base-content/60">
          Hear the original song and the custom version side by side
        </p>
      </div>

      {/* Audio Players */}
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <MiniPlayer
          url={originalAudioUrl}
          label="Original"
          accentClass="badge-primary"
        />
        <MiniPlayer
          url={deliveredAudioUrl}
          label="Custom Version"
          accentClass="badge-secondary"
        />
      </div>

      {/* Lyrics Side by Side */}
      <div className="px-6 pb-6">
        <p className="text-xs text-base-content/40 italic mb-3">
          Transcripts are auto-generated and may not perfectly reflect the audio.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="badge badge-primary badge-sm mb-2 font-bold">
              Original Lyrics
            </span>
            <div className="bg-base-200 rounded-xl p-4 text-sm leading-relaxed text-base-content/80 max-h-80 overflow-y-auto whitespace-pre-line">
              {originalLyrics}
            </div>
          </div>
          <div>
            <span className="badge badge-secondary badge-sm mb-2 font-bold">
              Custom Lyrics
            </span>
            <div className="bg-base-200 rounded-xl p-4 text-sm leading-relaxed text-base-content/80 max-h-80 overflow-y-auto whitespace-pre-line">
              {deliveredLyrics}
            </div>
          </div>
        </div>
      </div>

      {/* Customer Review */}
      {customerReview && (
        <div className="px-6 pb-6">
          <div className="bg-base-200 rounded-xl p-5 border border-base-300">
            <div className="flex items-center gap-1 mb-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <svg
                  key={i}
                  className={`w-4 h-4 ${
                    i < customerReview.rating
                      ? "text-warning"
                      : "text-base-content/20"
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <p className="text-base-content/80 text-sm italic mb-2">
              &ldquo;{customerReview.body}&rdquo;
            </p>
            <p className="text-base-content/60 text-xs font-medium">
              &mdash; {customerReview.author}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
